// Service de calcul de distance de préchargement adaptative
import type { NetworkSpeed } from '../Hooks/useNetworkSpeed';
import { LazyLoadLogger } from './LazyLoadLogger';

export interface PreloadDistanceConfig {
    baseDistance: number;           // Distance de base en px (défaut: 500)
    scrollSpeedThreshold: number;   // Seuil de vitesse pour augmenter (défaut: 1000 px/s)
    maxDistance: number;            // Distance maximale en px (défaut: 2000)
    minDistance: number;            // Distance minimale en px (défaut: 100)
}

class AdaptivePreloadServiceClass {
    private config: PreloadDistanceConfig = {
        baseDistance: 500,
        scrollSpeedThreshold: 1000,
        maxDistance: 2000,
        minDistance: 100
    };

    private lastDistance = 0;

    /**
     * Configure le service
     */
    configure(config: Partial<PreloadDistanceConfig>) {
        this.config = { ...this.config, ...config };
        LazyLoadLogger.logPreload('Configuration du service de préchargement', this.config);
    }

    /**
     * Calcule la distance de préchargement selon la vitesse de scroll et la connexion
     * 
     * @param scrollSpeed Vitesse de scroll actuelle en px/s (peut être 0)
     * @param networkSpeed Vitesse de connexion ('slow', 'medium', 'fast')
     * @param lastActiveSpeed Dernière vitesse significative de scroll (utilisée si scrollSpeed = 0)
     * @returns Distance en pixels (pour rootMargin de IntersectionObserver)
     */
    calculatePreloadDistance(
        scrollSpeed: number, 
        networkSpeed: NetworkSpeed,
        lastActiveSpeed: number = 0
    ): number {
        // Facteur réseau
        const networkFactor = this.getNetworkFactor(networkSpeed);
        
        // Utiliser lastActiveSpeed si scrollSpeed est à 0 ou très faible
        // Si les deux sont 0 (pas encore scrollé), on utilise quand même la distance de base
        const effectiveSpeed = scrollSpeed > 50 ? scrollSpeed : lastActiveSpeed;
        
        // Facteur scroll (plus on scroll vite, plus on précharge loin)
        // Si effectiveSpeed est 0, scrollFactor = 1.0 (distance de base)
        const scrollFactor = this.getScrollFactor(effectiveSpeed);
        
        // Calcul de la distance
        let distance = this.config.baseDistance * networkFactor * scrollFactor;
        
        // Borner la distance
        distance = Math.max(this.config.minDistance, Math.min(this.config.maxDistance, distance));
        
        // Logger si changement significatif (> 15%) OU première fois (lastDistance = 0)
        const distanceChange = Math.abs(distance - this.lastDistance) / (this.lastDistance || 1);
        const isFirstCalculation = this.lastDistance === 0;
        
        if (distanceChange > 0.15 || isFirstCalculation) {
            LazyLoadLogger.logPreload(`Distance de préchargement: ${distance.toFixed(0)}px${isFirstCalculation ? ' (initial)' : ''}`, {
                scrollSpeed: scrollSpeed.toFixed(0),
                lastActiveSpeed: lastActiveSpeed.toFixed(0),
                effectiveSpeed: effectiveSpeed.toFixed(0),
                networkSpeed,
                networkFactor: networkFactor.toFixed(2),
                scrollFactor: scrollFactor.toFixed(2),
                baseDistance: this.config.baseDistance,
                distance: distance.toFixed(0),
                change: isFirstCalculation ? 'initial' : `${(distanceChange * 100).toFixed(0)}%`
            });
            this.lastDistance = distance;
        }
        
        return Math.round(distance);
    }

    /**
     * Calcule le facteur basé sur la vitesse réseau
     */
    private getNetworkFactor(networkSpeed: NetworkSpeed): number {
        switch (networkSpeed) {
            case 'slow':
                return 0.5;  // Précharger moins loin sur connexion lente
            case 'medium':
                return 1.0;  // Distance normale
            case 'fast':
                return 1.5;  // Précharger plus loin sur connexion rapide
            default:
                return 1.0;
        }
    }

    /**
     * Calcule le facteur basé sur la vitesse de scroll
     */
    private getScrollFactor(scrollSpeed: number): number {
        if (scrollSpeed === 0) {
            return 1.0; // Pas de scroll, distance normale
        }

        // Augmenter progressivement jusqu'au seuil
        if (scrollSpeed < this.config.scrollSpeedThreshold) {
            // Facteur linéaire: 1.0 à 0 px/s → 1.5 au seuil
            return 1.0 + (scrollSpeed / this.config.scrollSpeedThreshold) * 0.5;
        }

        // Au-delà du seuil, augmenter plus modérément
        const excess = scrollSpeed - this.config.scrollSpeedThreshold;
        return 1.5 + Math.min(excess / 2000, 0.5); // Max 2.0
    }

    /**
     * Convertit la distance en format rootMargin pour IntersectionObserver
     */
    getAsRootMargin(distance: number): string {
        return `${distance}px 0px ${distance}px 0px`;
    }

    /**
     * Récupère la configuration actuelle
     */
    getConfig(): PreloadDistanceConfig {
        return { ...this.config };
    }
}

// Export singleton
export const AdaptivePreloadService = new AdaptivePreloadServiceClass();

